import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Check, AlertTriangle, Loader, Lock } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import SEO from "./SEO";
import { useTheme } from "../context/ThemeContext";
import { activateDirectoryAccount } from "../services/directoryService";
import "./Directory.css";

/**
 * Landing page for the one-time "Activate Your Account / Create Your Password"
 * link sent once an admin approves a directory submission. The account already
 * exists by the time this page is reached — all that is missing is a password.
 */
export default function DirectoryActivate() {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("The two passwords do not match.");
      return;
    }

    try {
      setSubmitting(true);
      await activateDirectoryAccount(token, password);
      setDone(true);
    } catch (err) {
      setError(err.message || "We could not activate this account.");
    } finally {
      setSubmitting(false);
    }
  };

  const panelStyle = {
    maxWidth: "480px",
    margin: "0 auto",
    padding: "64px 20px",
    textAlign: "center",
  };

  const inputStyle = {
    width: "100%",
    padding: "12px 14px",
    marginBottom: "14px",
    borderRadius: "10px",
    border: `1px solid ${darkMode ? "rgba(255,255,255,0.14)" : "#cbd5e1"}`,
    background: darkMode ? "#0f172a" : "#fff",
    color: darkMode ? "#e2e8f0" : "#0f172a",
    fontSize: "15px",
    fontFamily: "inherit",
    boxSizing: "border-box",
  };

  const iconWrap = (background, color) => ({
    width: "64px",
    height: "64px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 22px",
    background,
    color,
  });

  return (
    <div className={`directory-container ${darkMode ? "dark" : ""}`}>
      <SEO
        title="Activate Your Directory Account | LawFusion"
        path="/directory/activate"
        description="Set a password to manage your LawFusion directory listing."
        noIndex={true}
      />
      <Header />

      <main className="directory-content">
        <section style={panelStyle}>
          {!token ? (
            <>
              <div style={iconWrap(darkMode ? "rgba(239,68,68,0.18)" : "#fee2e2", "#dc2626")}>
                <AlertTriangle size={30} />
              </div>
              <h1 style={{ fontSize: "24px", fontWeight: 800, margin: "0 0 12px" }}>
                This link is incomplete
              </h1>
              <p style={{ color: darkMode ? "#94a3b8" : "#64748b", margin: "0 0 24px" }}>
                The activation link is missing its token. Please open the link
                from your approval email again.
              </p>
              <Link
                to="/directory"
                className="btn-join"
                style={{ textDecoration: "none", display: "inline-block" }}
              >
                Back to the Directory
              </Link>
            </>
          ) : done ? (
            <>
              <div style={iconWrap(darkMode ? "rgba(34,197,94,0.18)" : "#dcfce7", "#16a34a")}>
                <Check size={30} strokeWidth={2.5} />
              </div>
              <h1 style={{ fontSize: "24px", fontWeight: 800, margin: "0 0 12px" }}>
                Your account is ready
              </h1>
              <p style={{ color: darkMode ? "#94a3b8" : "#64748b", margin: "0 0 24px" }}>
                You can now log in to manage your directory listing.
              </p>
              <button
                type="button"
                className="btn-join"
                onClick={() => navigate("/login?area=directory")}
              >
                Log in to the Directory
              </button>
            </>
          ) : (
            <>
              <div style={iconWrap(darkMode ? "rgba(59,91,219,0.18)" : "#e0e7ff", "#4750d6")}>
                <Lock size={28} />
              </div>
              <h1 style={{ fontSize: "24px", fontWeight: 800, margin: "0 0 10px" }}>
                Create Your Password
              </h1>
              <p style={{ color: darkMode ? "#94a3b8" : "#64748b", margin: "0 0 26px" }}>
                Your listing has been approved. Choose a password to finish
                setting up your directory account.
              </p>

              <form onSubmit={handleSubmit} style={{ textAlign: "left" }}>
                <label htmlFor="da-password" style={{ fontSize: "13px", fontWeight: 700 }}>
                  Password
                </label>
                <input
                  id="da-password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  style={{ ...inputStyle, marginTop: "6px" }}
                />

                <label htmlFor="da-confirm" style={{ fontSize: "13px", fontWeight: 700 }}>
                  Confirm password
                </label>
                <input
                  id="da-confirm"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Re-enter your password"
                  style={{ ...inputStyle, marginTop: "6px" }}
                />

                {error && (
                  <div
                    style={{
                      padding: "10px 14px",
                      marginBottom: "14px",
                      borderRadius: "8px",
                      background: darkMode ? "rgba(239,68,68,0.15)" : "#fee2e2",
                      border: `1px solid ${darkMode ? "rgba(239,68,68,0.3)" : "#fca5a5"}`,
                      color: darkMode ? "#f87171" : "#991b1b",
                      fontSize: "13px",
                      fontWeight: 600,
                    }}
                  >
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  className="btn-join"
                  disabled={submitting}
                  style={{ width: "100%", opacity: submitting ? 0.7 : 1 }}
                >
                  {submitting ? (
                    <>
                      <Loader size={16} className="fa-spin" /> Activating…
                    </>
                  ) : (
                    "Activate My Account"
                  )}
                </button>
              </form>
            </>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
}
